import { Link } from "react-router-dom";
import { SEO } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import { FiCheck, FiAlertCircle, FiArrowRight } from "@/lib/icons";

interface Step {
  number: number;
  title: string;
  content: React.ReactNode;
}

const steps: Step[] = [
  {
    number: 1,
    title: "Complete the Contact Us page",
    content: (
      <>
        <p className="text-muted-foreground mb-4">
          Start your journey by completing our Contact Us page. We will be in touch
          within 24 hours during business days to discuss your needs and answer any
          questions you may have.
        </p>
        <div className="bg-accent/10 border border-accent/30 rounded-xl p-4 mt-4">
          <p className="text-sm font-medium flex items-center gap-2">
            <FiCheck className="size-4 text-primary" />
            Quick response within 24 business hours
          </p>
        </div>
      </>
    ),
  },
  {
    number: 2,
    title: "Complete our electronic Fact Find",
    content: (
      <>
        <p className="text-muted-foreground mb-4">
          Complete our comprehensive electronic Fact Find so that we can understand
          your full circumstances. This helps us assess your situation and find the
          best loan options tailored to your needs.
        </p>
        <div className="bg-accent/10 border border-accent/30 rounded-xl p-4 mt-4">
          <p className="text-sm font-medium flex items-center gap-2">
            <FiCheck className="size-4 text-primary" />
            Secure online form to capture all relevant details
          </p>
        </div>
      </>
    ),
  },
  {
    number: 3,
    title: "Initial interview",
    content: (
      <>
        <p className="text-muted-foreground mb-4">
          We'll meet you at your preferred time and location – home, office, or café,
          during the day, night, or weekend. This is your opportunity to share your
          needs and financial goals, and ask any questions about loans, applications,
          and approvals.
        </p>
        <div className="bg-accent/10 border border-accent/30 rounded-xl p-4 mt-4">
          <p className="text-sm font-medium flex items-center gap-2">
            <FiCheck className="size-4 text-primary" />
            Flexible meeting times and locations
          </p>
        </div>
      </>
    ),
  },
  {
    number: 4,
    title: "Arrange a pre-approved loan",
    content: (
      <>
        <p className="text-muted-foreground mb-4">
          If you haven't started your property search or are still looking, a
          pre-approved loan can be useful. It gives you a clear picture of what
          your spending limits are and gives you peace of mind that if you find
          a property you're really interested in you can move quickly to make an
          offer.
        </p>
        <div className="bg-accent/10 border border-accent/30 rounded-xl p-4 mt-4">
          <p className="text-sm font-medium flex items-center gap-2">
            <FiCheck className="size-4 text-primary" />
            Pre-approval typically lasts 3-6 months
          </p>
        </div>
      </>
    ),
  },
  {
    number: 5,
    title: "Find your property",
    content: (
      <>
        <p className="text-muted-foreground mb-4">
          Make sure you do plenty of homework when you're on the hunt for a new
          property. Research property prices in the area, potential capital
          growth and existing and planned infrastructure, such as roads, public
          transport, schools and shops.
        </p>
        <p className="text-muted-foreground">
          If you're unfamiliar with property values in the area, consider a full
          valuation carried out by a registered valuer before making a final
          decision.
        </p>
        <ul className="mt-4 space-y-2">
          {[
            "Research property prices in the area",
            "Check potential capital growth",
            "Review existing and planned infrastructure",
            "Consider a full valuation if unfamiliar with the area",
          ].map((item) => (
            <li
              key={item}
              className="flex items-start gap-3 text-muted-foreground text-sm"
            >
              <FiCheck className="size-4 text-primary mt-0.5 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    number: 6,
    title: "Appoint a conveyancer",
    content: (
      <>
        <p className="text-muted-foreground mb-4">
          You will need a conveyancer, solicitor or settlement agent to act for
          you to complete the sale.
        </p>
        <p className="text-muted-foreground mb-4">They'll be able to:</p>
        <ul className="space-y-2 mb-4">
          {[
            "Check all rates and taxes have been paid",
            "Check land use or building approvals for the property",
            "Order any relevant searches",
            "Help sort out any inspections",
          ].map((item) => (
            <li
              key={item}
              className="flex items-start gap-3 text-muted-foreground text-sm"
            >
              <FiCheck className="size-4 text-primary mt-0.5 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
        <p className="text-muted-foreground">
          On settlement day, the conveyancer will check the correct amount of
          money has been transferred from your lender to the seller and all fees
          – such as Stamp Duty – are paid, so you can take legal ownership of
          the property.
        </p>
      </>
    ),
  },
  {
    number: 7,
    title: "Make an offer and sign a Contract of Sale",
    content: (
      <>
        <p className="text-muted-foreground mb-4">
          When purchasing property, whether at auction or through an offer,
          you'll sign a Contract of Sale outlining the price and terms. This
          could include conditions like lender approval, building, and pest
          inspections.
        </p>
        <p className="text-muted-foreground mb-4">
          The settlement period, typically six weeks (shorter in some states
          like Queensland), finalises ownership.
        </p>
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mt-4">
          <p className="text-sm text-muted-foreground flex items-start gap-2">
            <FiAlertCircle className="size-4 text-primary mt-0.5 flex-shrink-0" />
            <span>
              <strong>Note:</strong> Even with a pre-approved loan, your lender
              requires a property valuation before granting full approval;
              unsatisfactory valuations may affect loan approval.
            </span>
          </p>
        </div>
      </>
    ),
  },
  {
    number: 8,
    title: "Pay a deposit",
    content: (
      <>
        <p className="text-muted-foreground mb-4">
          A deposit is required once a Contract of Sale has been signed by both
          parties. You won't yet have access to your home loan, so your deposit
          will need to come from savings or elsewhere.
        </p>
        <p className="text-muted-foreground">
          You may also be able to arrange a deposit bond until settlement.
        </p>
        <div className="bg-muted rounded-xl p-4 mt-4">
          <p className="text-sm font-medium mb-2">Typical deposit amounts:</p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• Standard deposit: 10% of purchase price</li>
            <li>• Some contracts accept: 5% deposit</li>
            <li>• Deposit bonds available as alternative</li>
          </ul>
        </div>
      </>
    ),
  },
  {
    number: 9,
    title: "Cooling-off period",
    content: (
      <>
        <p className="text-muted-foreground mb-4">
          If you didn't buy your property at auction, you may have a cooling-off
          period when you can cancel the contract, although there may be a small
          penalty.
        </p>
        <p className="text-muted-foreground">
          Cooling-off periods don't necessarily apply in every state, so check
          with your relevant state authority to find out what your rights may
          be.
        </p>
        <div className="bg-muted rounded-xl p-4 mt-4">
          <p className="text-sm font-medium mb-2">Cooling-off by state:</p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• NSW: 5 business days</li>
            <li>• VIC: 3 business days</li>
            <li>• QLD: 5 business days</li>
            <li>• Auctions: No cooling-off period</li>
          </ul>
        </div>
      </>
    ),
  },
  {
    number: 10,
    title: "Unconditional contracts",
    content: (
      <>
        <p className="text-muted-foreground mb-4">
          Be very cautious about signing an unconditional contract or bidding at
          an auction especially if you're not certain about whether you'll be
          able to obtain finance or about buying the home.
        </p>
        <p className="text-muted-foreground">
          You should also consider obtaining legal advice before signing a sale
          contract or bidding at an auction.
        </p>
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mt-4">
          <p className="text-sm text-muted-foreground flex items-start gap-2">
            <FiAlertCircle className="size-4 text-primary mt-0.5 flex-shrink-0" />
            <span>
              <strong>Important:</strong> An unconditional contract means you
              are legally bound to complete the purchase, even if your finance
              falls through. Always seek legal advice before signing.
            </span>
          </p>
        </div>
      </>
    ),
  },
];

export function LoanProcessGuidePage() {
  return (
    <>
      <SEO
        title="The Loan Process - Step by Step Guide"
        description="A comprehensive guide to understanding the home loan process in Australia. Learn about pre-approval, finding property, contracts, deposits, and settlement."
        url="/guides/loan-process"
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Link
            to="/guides"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
          >
            <FiArrowRight className="size-4 mr-2 rotate-180" />
            Back to Guides
          </Link>
          <p className="section-label mb-4">Guide</p>
          <h1 className="font-serif text-4xl md:text-5xl mb-6">
            Explaining the loan process
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Got some questions about the loan process? Sometimes the best way to
            get an understanding of home loans, how it all works and how to
            choose the right one, is to speak to a broker in person.
          </p>
          <div className="flex items-center gap-4 mt-6 text-sm text-muted-foreground">
            <span>10 min read</span>
            <span>•</span>
            <span>10 steps</span>
          </div>
        </div>
      </section>

      {/* Understanding Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div>
              <h2 className="font-serif text-2xl md:text-3xl mb-4">
                Understanding your needs
              </h2>
              <p className="text-muted-foreground mb-4">
                When you sit down with us, it's your opportunity to share your
                needs and financial goals. Ask questions about loans,
                applications, approvals, and what follows.
              </p>
              <p className="text-muted-foreground">
                We'll meet you at your preferred time and location – home,
                office, or café, during the day, night, or weekend.
              </p>
            </div>
            <div className="bg-cream rounded-2xl p-6">
              <p className="font-medium mb-3">In this guide:</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {steps.map((step) => (
                  <li key={step.number} className="flex items-start gap-2">
                    <span className="text-primary font-medium">
                      {step.number}.
                    </span>
                    {step.title}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">The Process</p>
            <h2 className="font-serif text-3xl md:text-4xl">
              How does the process work?
            </h2>
          </div>

          <div className="space-y-6">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className="relative pl-0 md:pl-16"
              >
                {/* Timeline line - desktop only */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute left-[1.375rem] top-14 bottom-0 w-px bg-border" />
                )}

                <div className="flex flex-col md:flex-row gap-4 md:gap-0">
                  {/* Step number */}
                  <div className="flex items-center gap-4 md:absolute md:left-0 md:top-0">
                    <div className="flex-shrink-0 w-11 h-11 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-serif text-lg font-medium">
                      {step.number}
                    </div>
                    <h3 className="font-serif text-xl md:text-2xl md:hidden">
                      {step.title}
                    </h3>
                  </div>

                  {/* Content card */}
                  <div className="bg-white border border-border/50 rounded-2xl p-6 md:p-8 flex-1 shadow-sm">
                    <h3 className="hidden md:block font-serif text-xl md:text-2xl mb-4">
                      {step.title}
                    </h3>
                    {step.content}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Need a home loan?"
        title="Whatever your circumstances, we will find the deal that's right for you"
        description="Send through a quick enquiry and we will be in touch."
        primaryAction={{ label: "Get in Touch", href: "/contact" }}
        secondaryAction={{ label: "View All Guides", href: "/guides" }}
      />
    </>
  );
}
